import { CustomAuthMethodTypeEnum } from '@pypestream/api-services';
import {
  Button,
  PageBody,
  PageSection,
  Result,
  Snackbar,
  Spacer,
  Spinner,
  Stack,
  TextTitle,
} from '@pypestream/design-system';
import { TranslationComponent, useTranslation } from '@pypestream/translations';
import { FC, useCallback, useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router';
import { OrganizationRoleNames, useAuthRole } from '../../../hooks';
import {
  sendManagerEvent,
  useManagerCtxSelector,
  useManagerStateMatches,
  useManagerStateMatchesAllOf,
  useManagerStateMatchesOneOf,
} from '../../../xstate/app.xstate';
import { IPage } from '../../types';
import { resolveOrgDepthLimit } from '../org-depth-limit';
import { useOrgLoadRequest } from '../use-org-load-request';
import {
  CustomAuthMethodSettingsType,
  DefaultAuthMethodSettingsType,
  ReturnFormType,
} from './create-organization-fields';
import { OrganizationDetails } from './organization-details';

export type FormOrgInfoType = {
  name: string;
  picture?: string;
  accountIconId?: string;
  accountManagerId?: string;
  customAuthMethods?: CustomAuthMethodSettingsType[];
  defaultAuthMethods?: DefaultAuthMethodSettingsType[];
};

export const OIDC_AUTH_METHOD_DEFAULT_VALUES = {
  name: 'Custom SSO via OpenID Connect',
  type: CustomAuthMethodTypeEnum.Oidc,
  isActive: false,
  isDefault: false,
  accountAuthMethodSettingId: '',
  clientId: '',
  clientSecret: '',
  issuerURL: '',
};

export const SAML_AUTH_METHOD_DEFAULT_VALUES = {
  name: 'Custom SSO via SAML2',
  type: CustomAuthMethodTypeEnum.Saml,
  isActive: false,
  isDefault: false,
  accountAuthMethodSettingId: '',
  settings: {
    nameIDPolicyFormat: '',
  },
  clientId: '',
  clientSecret: '',
  issuerURL: '',
  usernameAttribute: '',
  emailAttribute: '',
  entityIssuer: '',
};

// A module-level function so the hook's effect sees the same reference on every
// render.
const requestChildOrgsLoad = () =>
  sendManagerEvent('manager.childOrgs.loadChildOrgs');

export const CreateOrganization: FC<IPage> = () => {
  const navigate = useNavigate();
  const [t] = useTranslation();

  const { routes, availableDefaultAuthMethods, selectedOrgId, currentOrg } =
    useManagerCtxSelector((ctx) => ({
      routes: ctx.routes,
      availableDefaultAuthMethods: ctx.defaultAuthMethods,
      selectedOrgId: ctx.selectedOrgId,
      currentOrg: ctx.childOrgs.currentOrg,
    }));

  // PSTUDIO-4100. `childOrgs.currentOrg` is written by the child-orgs list's
  // load, so arriving here by URL (or after switching org) finds it missing or
  // describing another org. In that case the page asks for it itself, once per
  // org — a failed load must not be retried by an effect, or the machine would
  // loop through `error` -> `loading` for as long as the page stays open.
  const depthLimit = resolveOrgDepthLimit({ selectedOrgId, currentOrg });

  const isOrgLoadRequestable = useManagerStateMatchesAllOf([
    'orgRelated.ready.currentOrg.selected',
  ]);
  const isChildOrgsSettled = useManagerStateMatchesOneOf([
    'orgRelated.ready.childOrgs.idle',
    'orgRelated.ready.childOrgs.loaded',
  ]);
  const isChildOrgsLoading = useManagerStateMatches(
    'orgRelated.ready.childOrgs.loading'
  );

  const isOrgLoadDue = useOrgLoadRequest({
    selectedOrgId,
    depthLimit,
    canRequest: !!isOrgLoadRequestable && !!isChildOrgsSettled,
    requestLoad: requestChildOrgsLoad,
  });

  // The spinner covers every moment the answer is still on its way: the frame
  // before the request is sent, and the load itself.
  const isWaitingForOrgDepth =
    depthLimit === 'unknown' && (isOrgLoadDue || !!isChildOrgsLoading);

  // WCAG 2.4.3, as on the child-orgs list (PSTUDIO-4177): the spinner this
  // screen replaces held no focus, so the swap lands on <body>. Move focus to the
  // screen — but only if it really was dropped, so it never steals it from where
  // the user has since moved.
  const blockedScreenRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (depthLimit === 'blocked' && document.activeElement === document.body) {
      blockedScreenRef.current?.focus();
    }
  }, [depthLimit]);

  const isOrgAdding = useManagerStateMatches(
    'orgRelated.ready.orgDetails.adding'
  );

  const hasInflightNetworkRequest = useManagerStateMatchesOneOf([
    'orgRelated.ready.orgDetails.loading',
    'orgRelated.ready.orgDetails.updating',
    'orgRelated.ready.orgDetails.deleting',
    'orgRelated.ready.orgDetails.adding',
  ]);

  const isAdminRole = useAuthRole([
    OrganizationRoleNames.ADMIN,
    OrganizationRoleNames.SUPER_ADMIN,
  ]);

  const navigateToChildOrgs = useCallback(
    () => navigate(routes.orgs),
    [navigate, routes.orgs]
  );

  const form = useForm<FormOrgInfoType>({
    defaultValues: {
      name: '',
      picture: '',
      accountIconId: undefined,
      accountManagerId: '',
      customAuthMethods: [
        OIDC_AUTH_METHOD_DEFAULT_VALUES,
        SAML_AUTH_METHOD_DEFAULT_VALUES,
      ],
      defaultAuthMethods: [
        ...(availableDefaultAuthMethods || []).map((method) => ({
          id: method.id || '',
          name: method.name || '',
          isActive: false,
          isDefault: false,
          isCustomAuthMethod: false,
        })),
      ],
    },
  });

  useEffect(() => {
    if (
      availableDefaultAuthMethods &&
      !form.formState.defaultValues?.defaultAuthMethods?.length
    ) {
      form.resetField('defaultAuthMethods', {
        defaultValue: [
          ...(availableDefaultAuthMethods || []).map((method) => ({
            id: method.id || '',
            name: method.name || '',
            isActive: false,
            isDefault: false,
            isCustomAuthMethod: false,
          })),
        ],
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    availableDefaultAuthMethods,
    form.formState.defaultValues?.defaultAuthMethods,
  ]);

  const onSubmit = (values: FormOrgInfoType) => {
    sendManagerEvent({
      type: 'manager.addOrg',
      accountName: values.name,
      accountIconId: values.accountIconId,
      callback: (res) => {
        if (res) {
          sendManagerEvent('manager.childOrgs.loadChildOrgs');
          navigateToChildOrgs();
        }
      },

      defaultAuthMethods: (
        (values.defaultAuthMethods || []).filter(
          (defaultAuthMethod) => defaultAuthMethod.isActive
        ) || []
      ).map((defaultAuthMethod) => defaultAuthMethod.id!),
      customAuthMethods: values.customAuthMethods
        ?.filter((customAuthMethod) => customAuthMethod.isActive)
        .map(
          ({
            isActive,
            isDefault,
            accountAuthMethodSettingId,
            ...restFields
          }) => restFields
        ),
    });
  };

  // PSTUDIO-4100. Replaces the form rather than sitting beside it: a user who
  // reaches this route by URL must not be able to fill in a form that can only
  // be refused. `unknown` falls through to the form — the backend rule from
  // PSTUDIO-4097 is the guard for that window (see `resolveOrgDepthLimit`).
  if (depthLimit === 'blocked') {
    const blockedTitle =
      t('manager/organizations:createOrg.depthLimit.title', {
        defaultValue: "Child orgs can't be created here",
      }) || '';

    // The same error screen Pro Studio uses (`pages/error-page.tsx`): a centred
    // `Result` with one action. The wrapping div is a native node to move focus
    // to: `tabIndex={-1}` makes it focusable from script while keeping it out of
    // the tab order, the same way `recoveredSectionRef` does on the child-orgs
    // list. It is a named group so the move announces something. Its focus ring
    // is suppressed, as asked in the PSTUDIO-4100 review; the button inside
    // keeps its own. LIMITATION: the heading inside `Result` sits in nested
    // shadow roots, so focus cannot be put on the heading itself.
    return (
      <PageBody background="none">
        <div
          ref={blockedScreenRef}
          tabIndex={-1}
          role="group"
          aria-label={blockedTitle}
          style={{ outline: 'none' }}
        >
          <Result
            align="center"
            status="error"
            labelTag="h1"
            test-id="create-org-depth-limit"
            label={blockedTitle}
            body={
              t('manager/organizations:createOrg.depthLimit.body', {
                defaultValue:
                  'This org is already a child org. The hierarchy is limited to a parent org and one level of child orgs.',
              }) || ''
            }
          >
            <Stack gutter="xsmall" justifyContent="center" slot="footer">
              <Button
                test-id="create-org-depth-limit-back-btn"
                size="large"
                onClick={navigateToChildOrgs}
              >
                <TranslationComponent i18nKey="manager/organizations:createOrg.depthLimit.back">
                  Back to organizations
                </TranslationComponent>
              </Button>
            </Stack>
          </Result>
        </div>
      </PageBody>
    );
  }

  return (
    <PageBody background="none">
      <TextTitle
        test-id="create-org-title"
        i18nKey="manager/organizations:createOrg.title"
      >
        <TranslationComponent i18nKey="manager/organizations:createOrg.title">
          Create Child Org
        </TranslationComponent>
      </TextTitle>
      {((hasInflightNetworkRequest ||
        !availableDefaultAuthMethods ||
        isWaitingForOrgDepth) && (
        <PageSection>
          <Stack justifyContent="center" alignItems="center">
            <Spinner />
          </Stack>
        </PageSection>
      )) || (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
        >
          <Spacer size="large" />
          <OrganizationDetails
            form={form as unknown as ReturnFormType}
            isAdminRole={isAdminRole}
            isEdit={false}
            hasInflightNetworkRequest={!!hasInflightNetworkRequest}
          />
          <Snackbar
            id="cerate-org-snackbar"
            open={
              !!Object.keys(form.formState.dirtyFields).length && isAdminRole
            }
          >
            When you are ready, create the Org
            <Button
              test-id="cancel-creation-org-btn"
              variant="secondary"
              size="large"
              onClick={() => {
                form.reset();
              }}
              disabled={!!hasInflightNetworkRequest || isOrgAdding}
              slot="action"
            >
              <TranslationComponent i18nKey="manager/organizations:createOrg.cancel">
                Cancel
              </TranslationComponent>
            </Button>
            <Button
              test-id="create-org-btn"
              size="large"
              onClick={form.handleSubmit(onSubmit)}
              disabled={
                !Object.keys(form.formState.dirtyFields).length ||
                !!hasInflightNetworkRequest ||
                isOrgAdding
              }
              slot="action"
            >
              {hasInflightNetworkRequest || isOrgAdding ? (
                <TranslationComponent i18nKey="manager/organizations:createOrg.creating">
                  Creating Org...
                </TranslationComponent>
              ) : (
                <TranslationComponent i18nKey="manager/organizations:createOrg.create">
                  Create Org
                </TranslationComponent>
              )}
            </Button>
          </Snackbar>
        </form>
      )}
    </PageBody>
  );
};
